import {
  Component,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  OnChanges,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { ViewEncapsulation, ElementRef, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormArray, AbstractControl, FormControl } from '@angular/forms';

import { ReactiveFormsModule } from '@angular/forms';

import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { tap, map, startWith } from 'rxjs/operators';
import { retry, catchError } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';

import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { DeviceDetectorService } from 'ngx-device-detector';

import { DatePipe } from '@angular/common';

import { environment } from '../../environments/environment';
import { Logger } from '@core';
import { AuthQuery } from '@app/auth/auth.query';
import { JournalService, Journal, MailService, Mail } from '@core';
import { CookieService } from 'ngx-cookie-service';
import { AuthDataService } from '@app/auth/auth-data.service';

import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { KundeService, Kunde } from '../services/kunde.service';
import { DataChangeClientService } from '../client/changes-client.service';
import { Subscription } from 'rxjs';
import { MitarbeiterService, TechnicianOption } from '../services/mitarbeiter.service';


const log = new Logger('ClientEdit');

@Component({
  selector: 'app-client-edit',
  templateUrl: './client_edit.component.html',
  styleUrls: ['./client_edit.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
})
export class ClientEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  
  clientForm: UntypedFormGroup;
  client: Kunde | null = null;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  private subscription: Subscription;
  isNewClient = false;
  availableTechnicians: TechnicianOption[] = [];
  isLoadingTechnicians = false;
  environment = environment;

  // Getter for client status options from environment
  get clientStatusOptions() {
    return (environment as any).client_status || [];
  }

  get periodeAufgabenOptions() {
    return (environment as any).periode_aufgaben || [];
  }

  constructor(
    private kundeService: KundeService,
    private mitarbeiterService: MitarbeiterService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private authQuery: AuthQuery,
    private dataChangeClientService: DataChangeClientService,
    private datePipe: DatePipe
  ) {
    this.createForm();
  }

  ngOnInit() {
    console.log('ClientEditComponent ngOnInit called');
    
    // Load technicians for dropdown
    this.loadTechnicians();
    
    // Load client statuses for dropdown
    
    this.route.params.subscribe(params => {
      const id = params['id'];
      console.log('Route parameter id:', id);
      
      if (id === 'new' || id === '0') {
        console.log('Creating new client');
        this.isNewClient = true;
        this.clientForm.reset({
          suchbegriff: '',
          status_id: 2, // Default to inactive
          techniker_projekthinweis: '',
          startdatum: '',
          enddatum: '',
          anmeldung_besuche: '',
          periode_aufgaben_vorschlagen: 1, // Default to first option
          ueberpruefung_erforderlich: '',
          bemerkung_next_evaluation: '',
          wiederkehrende_aufgaben_vorhanden: 0,
          datum_letzte_wiederkehrende_aufgabe: '',
          techniker_id: null
        });
      } else {
        console.log('Editing existing client with UUID:', id);
        this.isNewClient = false;
        // Check if it's a UUID format (simple check)
        if (id && id.length > 10 && id.includes('-')) {
          this.loadClientByUuid(id);
        } else {
          console.error('Invalid client UUID:', id);
          this.snackBar.open('Invalid client UUID', 'Close', { duration: 3000 });
          this.router.navigate(['/clients']);
          return;
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // Restore tab state if returning from embedded component
    this.route.queryParams.subscribe(params => {
      if (params['activeTab']) {
        setTimeout(() => {
          if (this.tabGroup) {
            this.tabGroup.selectedIndex = parseInt(params['activeTab']);
          }
        }, 100);
      }
    });
  }

  createForm() {
    this.clientForm = this.formBuilder.group({
      suchbegriff: ['', [Validators.required]],
      status_id: [2],
      techniker_projekthinweis: [''],
      startdatum: [''],
      enddatum: [''],
      anmeldung_besuche: [''],
      periode_aufgaben_vorschlagen: [1],
      ueberpruefung_erforderlich: [''],
      bemerkung_next_evaluation: [''],
      wiederkehrende_aufgaben_vorhanden: [0],
      datum_letzte_wiederkehrende_aufgabe: [''],
      techniker_id: [null],
      // New address fields
      strasse: ['', [Validators.maxLength(255)]],
      plz: ['', [Validators.maxLength(10)]],
      ort: ['', [Validators.maxLength(255)]],
      land: ['', [Validators.maxLength(100)]],
      anmerkungen: ['']
    });
  }

  loadClient(id: number) {
    console.log('Loading client with ID:', id);
    this.isLoading = true;
    this.subscription = this.kundeService.getKunde(id).subscribe(
      (response) => {
        console.log('Client data received:', response);
        this.client = response.data;
        console.log('Setting form values with:', response.data);
        
        // More robust form patching with field mapping
        const formData = {
          suchbegriff: response.data.suchbegriff || '',
          status_id: response.data.status_id || 1,
          techniker_projekthinweis: response.data.techniker_projekthinweis || '',
          startdatum: response.data.startdatum || '', // Keep ISO format for date inputs
          enddatum: response.data.enddatum || '', // Keep ISO format for date inputs
          anmeldung_besuche: response.data.anmeldung_besuche || '',
          periode_aufgaben_vorschlagen: response.data.periode_aufgaben_vorschlagen || 0,
          ueberpruefung_erforderlich: response.data.ueberpruefung_erforderlich || '',
          bemerkung_next_evaluation: response.data.bemerkung_next_evaluation || '',
          wiederkehrende_aufgaben_vorhanden: response.data.wiederkehrende_aufgaben_vorhanden || 0,
          datum_letzte_wiederkehrende_aufgabe: response.data.datum_letzte_wiederkehrende_aufgabe || '', // Keep ISO format for date inputs
          techniker_id: response.data.techniker_id || null,
          // New address fields
          strasse: response.data.strasse || '',
          plz: response.data.plz || '',
          ort: response.data.ort || '',
          land: response.data.land || '',
          anmerkungen: response.data.anmerkungen || ''
        };
        
        console.log('Mapped form data:', formData);
        this.clientForm.patchValue(formData);
        console.log('Form values after patch:', this.clientForm.value);
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading client:', error);
        this.isLoading = false;
        this.snackBar.open(`Error loading client: ${error.status} ${error.statusText}`, 'Close', {
          duration: 5000,
        });
      }
    );
  }

  loadClientByUuid(uuid: string) {
    console.log('Loading client with UUID:', uuid);
    this.isLoading = true;
    this.subscription = this.kundeService.getKundeByUuid(uuid).subscribe(
      (response) => {
        console.log('Client data received:', response);
        this.client = response.data;
        console.log('Setting form values with:', response.data);
        
        // Only populate form if we're in edit mode or creating new client
        if (this.isEditMode || this.isNewClient) {
          // More robust form patching with field mapping
          const formData = {
            suchbegriff: response.data.suchbegriff || '',
            status_id: response.data.status_id || 1,
            techniker_projekthinweis: response.data.techniker_projekthinweis || '',
            startdatum: response.data.startdatum || '', // Keep ISO format for date inputs
            enddatum: response.data.enddatum || '', // Keep ISO format for date inputs
            anmeldung_besuche: response.data.anmeldung_besuche || '',
            periode_aufgaben_vorschlagen: response.data.periode_aufgaben_vorschlagen || 0,
            ueberpruefung_erforderlich: response.data.ueberpruefung_erforderlich || '',
            bemerkung_next_evaluation: response.data.bemerkung_next_evaluation || '',
            wiederkehrende_aufgaben_vorhanden: response.data.wiederkehrende_aufgaben_vorhanden || 0,
            datum_letzte_wiederkehrende_aufgabe: response.data.datum_letzte_wiederkehrende_aufgabe || '', // Keep ISO format for date inputs
            techniker_id: response.data.techniker_id || null,
            // New address fields
            strasse: response.data.strasse || '',
            plz: response.data.plz || '',
            ort: response.data.ort || '',
            land: response.data.land || '',
            anmerkungen: response.data.anmerkungen || ''
          };
          
          console.log('Mapped form data with ISO dates:', formData);
          this.clientForm.patchValue(formData);
          console.log('Form values after patch:', this.clientForm.value);
        }
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading client:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: error.url,
          body: error.body
        });
        this.isLoading = false;
        this.snackBar.open(`Error loading client: ${error.status} ${error.statusText}`, 'Close', {
          duration: 5000,
        });
      }
    );
  }

  saveClient() {
    if (this.clientForm.valid) {
      this.isSaving = true;
      const formData = this.clientForm.value;
      
      // Convert German dates back to ISO format for API
      const clientData = {
        ...formData,
        startdatum: this.germanToIsoDate(formData.startdatum),
        enddatum: this.germanToIsoDate(formData.enddatum),
        datum_letzte_wiederkehrende_aufgabe: this.germanToIsoDate(formData.datum_letzte_wiederkehrende_aufgabe)
      };

      if (this.isNewClient) {
        this.kundeService.createKunde(clientData).subscribe(
          (response) => {
            this.isSaving = false;
            
            // Update parent component via service
            this.dataChangeClientService.updateData(response.data);
            
            this.snackBar.open(
              'Client created successfully',
              'Close',
              { duration: 3000 }
            );
            
            // Navigate back to client list
            this.router.navigate(['/clients']);
          },
          (error) => {
            this.isSaving = false;
            this.snackBar.open(
              'Error creating client',
              'Close',
              { duration: 3000 }
            );
          }
        );
      } else {
        // For existing client, ensure we have a valid UUID
        const clientId = this.client?.uuid;
        if (!clientId) {
          this.snackBar.open('No valid client ID found', 'Close', { duration: 3000 });
          this.isSaving = false;
          return;
        }

        this.kundeService.updateKunde(clientId, clientData).subscribe(
          (response) => {
            this.isSaving = false;
            
            // Update the local client data with the response
            this.client = response.data;
            
            // Update parent component via service
            this.dataChangeClientService.updateData(response.data);
            
            // Switch back to view mode instead of redirecting
            this.isEditMode = false;
            
            this.snackBar.open(
              'Client updated successfully',
              'Close',
              { duration: 3000 }
            );
          },
          (error) => {
            this.isSaving = false;
            this.snackBar.open(
              'Error updating client',
              'Close',
              { duration: 3000 }
            );
          }
        );
      }
    }
  }

  cancel() {
    // Switch back to view mode instead of redirecting
    this.isEditMode = false;
  }

  goBackToList() {
    this.router.navigate(['/clients']);
  }

  deleteClient() {
    if (!this.client) {
      this.snackBar.open('No client to delete', 'Close', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Kunde löschen',
        message: `Sind Sie sicher, dass Sie den Kunden "${this.client.suchbegriff}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Use UUID for deletion if available, otherwise fall back to ID
        const identifier = this.client?.uuid || this.client?.id;
        
        if (!identifier) {
          this.snackBar.open('Keine gültige Client-ID gefunden', 'Close', { duration: 3000 });
          return;
        }
        
        this.kundeService.deleteKunde(identifier).subscribe(
          (response) => {
            this.snackBar.open('Kunde erfolgreich gelöscht', 'Close', {
              duration: 3000,
            });
            // Navigate back to client list
            this.router.navigate(['/clients']);
          },
          (error) => {
            console.error('Error deleting client:', error);
            this.snackBar.open('Fehler beim Löschen des Kunden', 'Close', {
              duration: 5000,
            });
          }
        );
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.clientForm.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  // Debug method to check form state
  debugFormState() {
    console.log('=== FORM DEBUG INFO ===');
    console.log('Form valid:', this.clientForm.valid);
    console.log('Form dirty:', this.clientForm.dirty);
    console.log('Form touched:', this.clientForm.touched);
    console.log('Form values:', this.clientForm.value);
    console.log('Form errors:', this.clientForm.errors);
    
    // Check each field
    Object.keys(this.clientForm.controls).forEach(key => {
      const control = this.clientForm.get(key);
      console.log(`Field ${key}:`, {
        value: control?.value,
        valid: control?.valid,
        dirty: control?.dirty,
        touched: control?.touched,
        errors: control?.errors
      });
    });
  }



  getStatusText(statusId: number): string {
    // Use environment client_status array
    const statusArray = (environment as any).client_status || [];
    const status = statusArray.find((s: any) => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  enterEditMode() {
    this.isEditMode = true;
    
    // Ensure technicians are loaded for the dropdown
    this.reloadTechniciansIfNeeded();
    
    // Patch the form with current client data
    if (this.client) {
      this.clientForm.patchValue({
        suchbegriff: this.client.suchbegriff || '',
        status_id: this.client.status_id || 1,
        techniker_projekthinweis: this.client.techniker_projekthinweis || '',
        startdatum: this.client.startdatum || '', // Keep ISO format for date inputs
        enddatum: this.client.enddatum || '', // Keep ISO format for date inputs
        anmeldung_besuche: this.client.anmeldung_besuche || '',
        periode_aufgaben_vorschlagen: this.client.periode_aufgaben_vorschlagen || 0,
        ueberpruefung_erforderlich: this.client.ueberpruefung_erforderlich || '',
        bemerkung_next_evaluation: this.client.bemerkung_next_evaluation || '',
        wiederkehrende_aufgaben_vorhanden: this.client.wiederkehrende_aufgaben_vorhanden || 0,
        datum_letzte_wiederkehrende_aufgabe: this.client.datum_letzte_wiederkehrende_aufgabe || '', // Keep ISO format for date inputs
        techniker_id: this.client.techniker_id || null,
        // Address fields
        strasse: this.client.strasse || '',
        plz: this.client.plz || '',
        ort: this.client.ort || '',
        land: this.client.land || '',
        anmerkungen: this.client.anmerkungen || ''
      });
      
      console.log('Form patched in edit mode with ISO dates:', {
        startdatum: this.client.startdatum,
        enddatum: this.client.enddatum,
        datum_letzte_wiederkehrende_aufgabe: this.client.datum_letzte_wiederkehrende_aufgabe
      });
    }
  }

  cancelEdit() {
    this.isEditMode = false;
  }

  /**
   * Load all available technicians for dropdown
   */
  loadTechnicians(): void {
    console.log('Loading technicians...');
    this.isLoadingTechnicians = true;
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        console.log('Technicians loaded:', response);
        this.availableTechnicians = response.data || [];
        this.isLoadingTechnicians = false;
        console.log('Available technicians count:', this.availableTechnicians.length);
      },
      error: (error) => {
        console.error('Error loading technicians:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoadingTechnicians = false;
      }
    });
  }

  /**
   * Get technician display name by ID
   */
  getTechnicianDisplayName(technikerId: string | number): string {
    if (!technikerId) {
      return '-';
    }
    
    if (!this.availableTechnicians.length) {
      return `Techniker ID: ${technikerId}`;
    }
    
    const technician = this.availableTechnicians.find(tech => tech.id === Number(technikerId));
    if (!technician) {
      return `Techniker ID: ${technikerId} (nicht gefunden)`;
    }
    
    return `${technician.display_name} (${technician.nummer}) - ${technician.funktion}`;
  }

  /**
   * Reload technicians if needed (e.g., when entering edit mode)
   */
  reloadTechniciansIfNeeded(): void {
    if (!this.availableTechnicians.length && !this.isLoadingTechnicians) {
      this.loadTechnicians();
    }
  }

  /**
   * Get the current active tab index
   */
  getCurrentTabIndex(): number {
    return this.tabGroup?.selectedIndex || 0;
  }

  /**
   * Convert ISO date (YYYY-MM-DD) to German format (DD.MM.YYYY)
   */
  private isoToGermanDate(isoDate: string): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return isoDate; // Return original if invalid
    return this.datePipe.transform(date, 'dd.MM.yyyy') || isoDate;
  }

  /**
   * Convert German date (DD.MM.YYYY) to ISO format (YYYY-MM-DD)
   */
  private germanToIsoDate(germanDate: string): string {
    if (!germanDate) return '';
    // Check if it's already in ISO format
    if (germanDate.match(/^\d{4}-\d{2}-\d{2}$/)) return germanDate;
    
    // Convert from DD.MM.YYYY to YYYY-MM-DD
    const parts = germanDate.split('.');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return germanDate;
  }

  /**
   * Format date for display in German format
   */
  formatDateForDisplay(dateString: string): string {
    return this.isoToGermanDate(dateString);
  }
}
